import { OPTIONS as ROOT_OPTIONS } from '../../constants';
import { isAddressComplete } from '../../components/address';

const pronoun = movingCompany => (movingCompany ? 'we' : 'the movers');
const addressPhrase = address =>
  isAddressComplete(address)
    ? 'Confirm or edit the address'
    : 'Enter the address';

export const ROUTES = {
  complete: '/local-complete',
  fromAddress: '/old-address',
  fromDistanceToCurb: '/old-parking',
  fromElevators: '/old-stairs',
  fromStairCount: '/old-flights',
  homeSize: '/old-home-size',
  longDistance: '/long-distance-complete',
  outOfServiceArea: '/out-of-service-area',
  largeHome: '/large-home-complete',
  toAddress: '/new-address',
  toDistanceToCurb: '/new-parking',
  toElevators: '/new-stairs',
  toStairCount: '/new-flights',
};

export const HOME_DETAILS_ROUTES = ROUTES;

// note to developer: Changing order of the last route here
// requres update in the main `home-details` component
// as its hard coded to expect toDistanceToCurb to be the last route.
export const ROUTES_SEQUENCE = [
  ROUTES.fromElevators,
  ROUTES.fromStairCount,
  ROUTES.fromDistanceToCurb,
  ROUTES.toElevators,
  ROUTES.toStairCount,
  ROUTES.toDistanceToCurb,
  ROUTES.complete,
  ROUTES.largeHome,
  ROUTES.longDistance,
  ROUTES.outOfServiceArea,
];

export const HOME_DETAILS_ROUTES_SEQUENCE = ROUTES_SEQUENCE;

export const NAMES = {
  address: 'address',
  distanceToCurb: 'distanceToCurb',
  elevators: 'elevators',
  homeSize: 'homeSize',
  stairCount: 'stairCount',
};

const prependFrom = name => `fromAddressDetails.${name}`;
const prependTo = name => `toAddressDetails.${name}`;

export const ROUTES_FORM_NAMES = {
  [ROUTES.fromAddress]: prependFrom(NAMES.address),
  [ROUTES.fromDistanceToCurb]: prependFrom(NAMES.distanceToCurb),
  [ROUTES.fromElevators]: prependFrom(NAMES.elevators),
  [ROUTES.fromStairCount]: prependFrom(NAMES.stairCount),
  [ROUTES.homeSize]: NAMES.homeSize,
  [ROUTES.toAddress]: prependTo(NAMES.address),
  [ROUTES.toDistanceToCurb]: prependTo(NAMES.distanceToCurb),
  [ROUTES.toElevators]: prependTo(NAMES.elevators),
  [ROUTES.toStairCount]: prependTo(NAMES.stairCount),
};

// prettier-ignore
export const TITLES = {
  [ROUTES.homeSize]: ({ currentMove: { movingCompany }}) => html => html`${movingCompany? '' : 'Let\'s start!' } What size is the home you are moving from?`,

  [ROUTES.fromAddress]: ({ currentMove: { movingCompany }, moveIqMove: { fromAddressDetails: { address } } }) => html =>
    html`<span>${addressPhrase(address)} where ${pronoun(movingCompany)} should <strong>pick up</strong> your things.</span>`,

  [ROUTES.fromElevators]: ({ currentMove: { movingCompany } }) => html =>
    html`<span>Will ${pronoun(movingCompany)} need to use stairs to access your <strong>old</strong> home?</span>`,

  [ROUTES.fromStairCount]: () => html =>
    html`<span>How many flights of stairs are there at your <strong>old</strong> home?</span>`,

  [ROUTES.fromDistanceToCurb]: ({ currentMove: { movingCompany } }) => html =>
    html`<span>Where will ${pronoun(movingCompany)} be able to park the truck at your <strong>old</strong> home?</span>`,

  [ROUTES.toAddress]: ({ currentMove: { movingCompany }, moveIqMove: { toAddressDetails: { address } } }) => html =>
    html`<span>${addressPhrase(address)} where ${pronoun(movingCompany)} should <strong>drop off</strong> your things.</span>`,

  [ROUTES.toElevators]: ({ currentMove: { movingCompany } }) => html =>
    html`<span>Will ${pronoun(movingCompany)} need to use stairs to access your <strong>new</strong> home?</span>`,

  [ROUTES.toStairCount]: () => html =>
    html`<span>How many flights of stairs are there at your <strong>new</strong> home?</span>`,

  [ROUTES.toDistanceToCurb]: ({ currentMove: { movingCompany } }) => html =>
    html`<span>Where will ${pronoun(movingCompany)} be able to park the truck at your <strong>new</strong> home?</span>`,

  [ROUTES.complete]: () => html => html`Home details complete!`,

  [ROUTES.largeHome]: () => html => html`3+ Bedroom Moves`,

  [ROUTES.longDistance]: () => html => html`Long Distance Moves`,
};

export const DESCRIPTIONS = {
  [ROUTES.complete]: () => html =>
    html`
      Next, we'll make a list of items that need to be moved to your new home.
      We've started you off by adding things commonly found in homes like yours.
    `,

  [ROUTES.largeHome]: ({
    currentMove: { movingCompany },
    moveIqMove: {
      toAddressDetails: {
        address: { city },
      },
    },
  }) => html => {
    const reference = movingCompany ? '' : 'with a moving company';
    const outro = movingCompany
      ? 'Contact us to arrange a convenient time.'
      : `Browse Updater approved moving companies that can service your move to ${city}.`;

    html`
      For your move you’ll need to schedule an in-home estimate ${reference} in
      order to get an accurate price. ${outro}
    `;
  },

  [ROUTES.longDistance]: ({
    currentMove: { movingCompany },
    moveIqMove: {
      toAddressDetails: {
        address: { city },
      },
    },
  }) => html =>
    movingCompany
      ? html`
          For non-local moves you'll need to schedule an in-home estimate in
          order to get an accurate price. Contact us to arrange a convenient
          time.
        `
      : html`
          For your move you’ll need to schedule an in-home estimate with a
          moving company in order to get an accurate estimate. Browse Updater
          approved moving companies that can service your move to ${city}.
        `,

  [ROUTES.outOfServiceArea]: ({
    currentMove: { movingCompany },
    moveIqMove: {
      fromAddressDetails: {
        address: { postalCode: fromZip },
      },
      toAddressDetails: {
        address: { postalCode: toZip },
      },
    },
  }) => html => {
    const reason = movingCompany
      ? `We don’t provide service from ${fromZip} to ${toZip}.`
      : `There are no moving companies in our system that provide service from ${fromZip} to ${toZip}.`;

    html`
      <upd-moveiq-custom-icon-title-message>
        <upd-fa-icon slot="icon" name="exclamation-triangle"></upd-fa-icon>
        <div slot="title">Out of Service Area</div>
        <div slot="message">
          Oh no! ${reason} If you have flexibility in your move, you can try
          another address.
        </div>
      </upd-moveiq-custom-icon-title-message>
    `;
  },
};

export const OPTIONS = {
  [NAMES.homeSize]: ROOT_OPTIONS.homeSize,
  [NAMES.elevators]: ROOT_OPTIONS.elevators,
  [NAMES.stairCount]: ROOT_OPTIONS.stairCount,
  [NAMES.distanceToCurb]: ROOT_OPTIONS.distanceToCurb,
};

export const HELPER_TEXTS = {
  [NAMES.stairCount]:
    'Understanding how many flights of stairs movers will have to climb is important for accurate pricing',
};

export const ANALYTICS_EVENTS = {
  [ROUTES.homeSize]: 'Old Home Size',
  [ROUTES.fromAddress]: 'Old Address',
  [ROUTES.fromElevators]: 'Old Stairs',
  [ROUTES.fromStairCount]: 'Old Flights',
  [ROUTES.fromDistanceToCurb]: 'Old Parking',
  [ROUTES.toAddress]: 'New Address',
  [ROUTES.toElevators]: 'New Stairs',
  [ROUTES.toStairCount]: 'New Flights',
  [ROUTES.toDistanceToCurb]: 'New Parking',
  [ROUTES.complete]: 'Local Complete',
  [ROUTES.largeHome]: 'Large Home Complete',
  [ROUTES.longDistance]: 'Long Distance Complete',
  [ROUTES.outOfServiceArea]: 'Out of Service Area',
};
